import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, Select, MenuItem, Chip, Tooltip } from '@mui/material';
// icons
import DeleteIcon from 'common/customIcons/delete';
import AngleRightIcon from "common/customIcons/angle_right";
import InformationIcon from "common/customIcons/informationIcon";
// utils and helpers
import { grey } from '@mui/material/colors';
import { formattedDate } from "utilities/common";
import { templateKitValidation } from "validations/hyperwolf/templateKit";
import { addTemplateKit, getBoxes, getDistributionConfig, getTemplateKitDetails, getUnassignedRegions, updateTemplateKit } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from "../../../../redux/slices/common";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from 'components/MDButton';
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";

const initialValues = {
  regionId: "",
  subRegionId: [],
  minProductPerRegion: "",
  maxProductPerRegion: "",
  createdBy: "adminUser",
  isActive: true,
  platform: "hyperwolf",
  boxes: [
    { boxId: "", minProductValuePerBox: "", maxProductValuePerBox: "" }
  ]
}

const CreateTemplate = () => {
  const { templateId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [defaultConfig, setDefaultConfig] = useState({})
  const [details, setDetails] = useState({ ...initialValues })
  const [isLoading, setIsLoading] = useState(false)
  const [boxesList, setBoxesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [errors, setErrors] = useState({});

  const handleGetConfig = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data
      if (data) {
        setDefaultConfig(data || {})

        setDetails(prev => ({
          ...prev,
          minProductPerRegion: data?.minProductPerRegion,
          maxProductPerRegion: data?.maxProductPerRegion,
          boxes: [
            { boxId: "", minProductValuePerBox: data.minProductPerBox, maxProductValuePerBox: data?.maxProductPerBox }
          ]
        }))
      }
    } catch (error) {
    }
  }

  const handleTemplateKitDetails = async () => {
    try {
      const response = await dispatch(getTemplateKitDetails({ templateId })).unwrap()
      if (response?.data) {
        setDetails({ ...response.data })
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleBoxList = async () => {
    try {
      const response = await dispatch(getBoxes({ limit: 200 })).unwrap();
      if (response?.data) {
        setBoxesList(response?.data || [])
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleRegionsList = async () => {
    try {
      const request = { limit: 1000 };
      if (templateId) {
        request.templateId = templateId
      }
      const response = await dispatch(getUnassignedRegions(request)).unwrap()
      if (response?.data) {
        setRegionsList(response?.data || [])
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleTopLevelChange = (field) => (event) => {
    const valueRaw = event.target.value;
    let value = valueRaw;
    if (field === 'minProductPerRegion' || field === 'maxProductPerRegion') {
      value = valueRaw === "" ? "" : valueRaw?.replace(/[^0-9]/g, "");
    }
    if (field === 'isActive') {
      value = event.target.checked;
    }
    if (field === 'regionId') {
      setDetails((prev) => ({ ...prev, [field]: value, subRegionId: [] }));
    } else {
      setDetails((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleBoxChange = (index, field) => (event) => {
    const valueRaw = event.target.value;
    let value = valueRaw;
    if (field === 'minProductValuePerBox' || field === 'maxProductValuePerBox') {
      value = valueRaw === "" ? "" : valueRaw?.replace(/[^0-9]/g, "");
    }
    setDetails((prev) => {
      const updated = [...prev.boxes];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, boxes: updated };
    });
  };

  const addBox = () => {
    setDetails((prev) => ({
      ...prev,
      boxes: [
        ...prev.boxes,
        { boxId: "", minProductValuePerBox: defaultConfig?.minProductPerBox || "", maxProductValuePerBox: defaultConfig?.maxProductPerBox || "" }
      ]
    }));
  };

  const removeBox = (index) => {
    setDetails((prev) => ({
      ...prev,
      boxes: prev.boxes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const normalizedDetails = {
        ...details,
        minProductPerRegion: Number(details?.minProductPerRegion || 0),
        maxProductPerRegion: Number(details?.maxProductPerRegion || 0),
        boxes: (details.boxes || []).filter((b) => b.boxId)
      };

      const { isValid, myErrors } = templateKitValidation(normalizedDetails);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      let response = {};
      if (templateId) {
        response = await dispatch(updateTemplateKit(normalizedDetails)).unwrap();
      } else {
        response = await dispatch(addTemplateKit(normalizedDetails)).unwrap();
      }

      if (response?.data || response?.message) {
        showToastMsg(response?.message || "Success", "success");
        navigate(`/hyperwolf/kit-template/create-template/assign-products/${response?.data?._id}?updating=${templateId ? true : false}`);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning" }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleBoxList();
    handleRegionsList();
    handleGetConfig();
  }, [])

  useEffect(() => {
    if (templateId) {
      handleTemplateKitDetails()
    }
  }, [templateId]);

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Template
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="semibold" mb={2}>
                Page Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Creation Date</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name=""
                    className="form-input created-by p-l-field bg-transparent"
                    disabled
                    value={formattedDate(new Date(details?.createdDate || Date.now()))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Region Name</Typography>
                  <Select
                    size='large'
                    fullWidth
                    className='form-select'
                    name="regionId"
                    value={details.regionId}
                    label=''
                    onChange={handleTopLevelChange('regionId')}
                    MenuProps={{
                      classes: {
                        root: "cus-dropdown-height",
                      }
                    }}
                  >
                    {
                      regionsList?.length > 0 ?
                        regionsList?.map(region => (
                          <MenuItem key={region?.regionId} value={region.regionId}>
                            {region.region}
                          </MenuItem>
                        ))
                        : <MenuItem value={null} selected disabled>
                          No regions left for template
                        </MenuItem>
                    }
                  </Select>
                  {errors?.regionId && <p className="error">{errors?.regionId}</p>}
                </Grid>
                <Grid item xs={12} md={4} className="cus-multi-select full-multi-select">
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>
                    Sub Region Name
                    <Tooltip arrow title={"Only Sub Regions with assigned driver can be used."}>
                      <span>
                        <InformationIcon style={{ width: '18px', height: '18px', }} />
                      </span>
                    </Tooltip>
                  </Typography>

                  <Select
                    size='large'
                    fullWidth
                    className='form-select'
                    label=''
                    multiple={true}
                    name="subRegionId"
                    value={details.subRegionId}
                    onChange={handleTopLevelChange('subRegionId')}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const subRegion = regionsList?.find(region => region.regionId === details.regionId)?.subRegions?.find(d => d.blazeRegionId === id);
                          if (!subRegion) return null;

                          return (
                            <Chip
                              key={subRegion.subRegionId}
                              label={subRegion.name}
                              onDelete={(event) => {
                                event.stopPropagation();
                                const newSubRegions = details.subRegionId?.filter(id => id !== subRegion.blazeRegionId);
                                handleTopLevelChange('subRegionId')({ target: { name: "subRegionId", value: newSubRegions } })
                              }}
                              sx={{ m: 0.25 }}
                              // Prevent chip click from opening dropdown
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => e.stopPropagation()}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {
                      !details.regionId
                        ? <MenuItem value={""} disabled>
                          Select Region First
                        </MenuItem>
                        : regionsList?.find(region => region.regionId === details.regionId)?.subRegions?.map(subRegion => (
                          <MenuItem key={subRegion?.subRegionId} value={subRegion.blazeRegionId}>
                            {subRegion.name}
                          </MenuItem>
                        ))
                    }
                  </Select>
                  {errors?.subRegionId && <p className="error">{errors?.subRegionId}</p>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Per Region</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    className="form-input created-by p-l-field bg-transparent"
                    placeholder="Min Products Per Region"
                    name="minProductPerRegion"
                    value={details.minProductPerRegion}
                    onChange={handleTopLevelChange('minProductPerRegion')}
                  />
                  {errors?.minProductPerRegion && <p className="error">{errors?.minProductPerRegion}</p>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Per Region</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    className="form-input created-by p-l-field bg-transparent"
                    placeholder="Max Products Per Region"
                    name="maxProductPerRegion"
                    value={details.maxProductPerRegion}
                    onChange={handleTopLevelChange('maxProductPerRegion')}
                  />
                  {errors?.maxProductPerRegion && <p className="error">{errors?.maxProductPerRegion}</p>}
                </Grid>
                {/* <Grid item xs={12} md={4}>
                                <FormControlLabel control={<Switch color="primary" checked={Boolean(details.isActive)} onChange={handleTopLevelChange('isActive')} />} label="Is Active" />
                            </Grid> */}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <hr className='my-3' style={{ borderTop: "1px solid #F1F1F1", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
            </Grid>

            {/* Box specific limit */}
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="semibold">
                Add Boxes For Specific Limits
              </Typography>
              {(details.boxes || []).map((box, index) => (
                <Grid key={index} container spacing={3} className="mt-0 pr-5 position-relative">
                  <Grid item xs={12} md={4}>
                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Box</Typography>
                    <Select
                      size='large'
                      fullWidth
                      className='form-select'
                      name={`boxes.${index}.boxId`}
                      value={box.boxId}
                      label=''
                      onChange={handleBoxChange(index, 'boxId')}
                      MenuProps={{
                        classes: {
                          root: "cus-dropdown-height",
                        }
                      }}
                    >
                      {
                        boxesList
                          ?.filter(box =>
                            // Only show boxes that are not selected in any other box
                            !details.boxes.some((b, i) => b.boxId === box._id && i !== index)
                          )
                          .map(box => (
                            <MenuItem key={box?._id} value={box?._id}>
                              {box?.name}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Min Products Value For This Box</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      className="form-input created-by p-l-field bg-transparent"
                      placeholder="Min Products Value For This Box"
                      name={`boxes.${index}.minProductValuePerBox`}
                      value={box.minProductValuePerBox}
                      onChange={handleBoxChange(index, 'minProductValuePerBox')}
                    />
                    {errors?.boxes?.[index]?.minProductValuePerBox && <p className="error">{errors.boxes[index].minProductValuePerBox}</p>}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Max Products Value For This Box</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      className="form-input created-by p-l-field bg-transparent"
                      placeholder="Max Products Value For This Box"
                      name={`boxes.${index}.maxProductValuePerBox`}
                      value={box.maxProductValuePerBox}
                      onChange={handleBoxChange(index, 'maxProductValuePerBox')}
                    />
                    {errors?.boxes?.[index]?.maxProductValuePerBox && <p className="error">{errors.boxes[index].maxProductValuePerBox}</p>}
                  </Grid>
                  <IconButton
                    className="position-absolute right-0"
                    style={{ top: "60px" }} color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}
                    onClick={() => removeBox(index)}>
                    <DeleteIcon width='16px' height='16px' />
                  </IconButton>
                </Grid>
              ))}

              {boxesList?.length > details.boxes?.length &&
                <Grid container spacing={3} className="mt-0">
                  <Grid item xs={12} md={12}>
                    <MDButton
                      variant="contained"
                      sx={{ minWidth: '150px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                      onClick={addBox}
                    >
                      + Add box specific limits
                    </MDButton>
                  </Grid>
                </Grid>}
              {errors?.boxesRequired && <p className="error">{errors?.boxesRequired}</p>}
            </Grid>
          </Grid>

          {/********************** Save, Cancel Buttons **********************/}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={6} mb={2}>
                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Next'}
                </MDButton>
                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MDButton>
              </Stack>
            </Grid>
          </Grid>
        </Card>

      </Box>
    </CommonLayout>
  )
}

export default CreateTemplate
